import React, { useContext } from 'react';
import { Typography, Divider, Price, RegularButton } from 'fronton-react';
import { WarningIcon } from '@fronton/icons-react';
import { useRecoilValue } from 'recoil';
import { TProjectConfiguration } from 'pages/common/types';
import { DataContext } from 'entries/common/widgets/saved-project-configuration/context';
import { Cart } from '../Cart';
import { useTotalPrices } from '../../hooks/use-total-prices';
import { savedConfigurationCalculationState } from '../../store/calculation';
import { getGeneralItemsFromCalculationState } from '../../helpers/get-general-items-from-calculation-state';
import { usePatchProjectFromCalculationState } from '../../hooks/use-patch-project-from-calculation-state';
import { useRegionMismatch } from './hooks/use-region-mismatch';
import {
  AddToCartWrapper,
  ResultSumWrapper,
  PriceResultClassname,
  RegionMismatchContainer,
  PresaleActionsContainer,
  PresaleActionItem,
} from './styles';

export const AddToCart = () => {
  const { data } = useContext(DataContext);
  const calculationFormState = useRecoilValue(savedConfigurationCalculationState);
  const {
    mainProductTotalPrice,
    isShowComplementaryProductsTotalPrice,
    complementaryProductsTotalPrice,
    totalPrice,
  } = useTotalPrices();
  const { isRegionMismatch } = useRegionMismatch();
  const { patchProjectCallBack } = usePatchProjectFromCalculationState();
  const {
    onQuotationButtonClick,
    onCartButtonClick,
    isCartButtonLoading = false,
    isQuotationButtonLoading = false,
  } = data?.presaleProps ?? {};

  const projectForPresale: TProjectConfiguration | null = data
    ? {
        id: data.id,
        createdDate: '',
        projectConfigurationParameters: data.projectConfigurationParameters ?? [],
        projectStatus: data.projectStatus,
        approximateTotalAmount: totalPrice,
        regionId: data.regionId,
        generalItems: getGeneralItemsFromCalculationState(calculationFormState),
      }
    : null;
  const shouldShowPresaleActions = Boolean(
    projectForPresale && (onQuotationButtonClick || onCartButtonClick)
  );
  return (
    <AddToCartWrapper data-testid="add-to-cart">
      <ResultSumWrapper>
        <Typography size="body_short" variant="s" color="text-primary">
          Основной товар
        </Typography>
        <Price
          align="right"
          size="s"
          value={mainProductTotalPrice}
          variant="regular"
          className={PriceResultClassname}
          fraction
        />
      </ResultSumWrapper>
      {isShowComplementaryProductsTotalPrice ? (
        <ResultSumWrapper>
          <Typography size="body_short" variant="s">
            Расходники и инструменты
          </Typography>
          <Price
            align="right"
            size="s"
            value={complementaryProductsTotalPrice}
            variant="regular"
            className={PriceResultClassname}
            fraction
          />
        </ResultSumWrapper>
      ) : null}

      <Divider orientation="horizontal" variant="line" />
      <ResultSumWrapper isLast>
        <Typography size="body_accent" variant="l">
          Итого
        </Typography>
        <Price
          align="right"
          size="xl"
          value={totalPrice}
          variant="medium"
          data-testid="calculation-price"
          fraction
        />
      </ResultSumWrapper>
      {isRegionMismatch ? (
        <RegionMismatchContainer>
          <WarningIcon type="fill" size="s" color="attention-primary" />
          <Typography size="caption" variant="minor" color="text-secondary">
            Расчёт был сделан в другом регионе. Цены в корзине могут измениться
          </Typography>
        </RegionMismatchContainer>
      ) : null}
      {shouldShowPresaleActions ? (
        <PresaleActionsContainer>
          {onCartButtonClick ? (
            <PresaleActionItem>
              <RegularButton
                wide
                size="m"
                variant="primary"
                loading={isCartButtonLoading}
                disabled={isCartButtonLoading}
                onClick={() => onCartButtonClick(projectForPresale!, patchProjectCallBack)}>
                В корзину
              </RegularButton>
            </PresaleActionItem>
          ) : null}
          {onQuotationButtonClick ? (
            <PresaleActionItem>
              <RegularButton
                wide
                size="m"
                variant="primary"
                loading={isQuotationButtonLoading}
                disabled={isQuotationButtonLoading}
                onClick={() => onQuotationButtonClick(projectForPresale!, patchProjectCallBack)}>
                В смету
              </RegularButton>
            </PresaleActionItem>
          ) : null}
        </PresaleActionsContainer>
      ) : (
        <Cart />
      )}
    </AddToCartWrapper>
  );
};
